import type { Action, ActionResult } from '../types/actions';
import type { UIHighlighter } from '../highlighter/ui-highlighter';
import { waitForElement } from '../utils/dom-utils';

/**
 * VisualHighlight action: pulse + glow overlay on the target element for ~3.5s
 * AND auto-focus the cursor inside if it's an input. This is the action the
 * `highlight_element` MCP tool dispatches — distinct from the `focus` action
 * (which only synthesises focus events for the voice-widget workaround).
 */
export async function executeVisualHighlightAction(
  action: Extract<Action, { type: 'visualHighlight' }>,
  highlighter: UIHighlighter
): Promise<ActionResult> {
  const start = Date.now();
  try {
    let element: Element | null = action.selector
      ? document.querySelector(action.selector)
      : null;
    if (!element && action.selector) {
      element = await waitForElement(action.selector, 1500).catch(() => null);
    }
    if (!element) {
      return {
        success: false,
        action,
        error: `Element not found: ${action.selector}`,
        timestamp: start,
        duration: Date.now() - start,
      };
    }
    const htmlEl = element as HTMLElement;

    // Centre the element vertically before drawing the overlay so the user
    // actually sees it. dom-utils.scrollToElement / element.scrollIntoView with
    // block: 'center' keeps tall sections off the top edge.
    try {
      htmlEl.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
    } catch {
      // Older browsers — fall back to default scrollIntoView
      htmlEl.scrollIntoView();
    }

    // Draw the pulse + glow overlay. UIHighlighter.highlight() also auto-
    // focuses inputs (it checks the tag name internally), so the caret
    // appears alongside the visual indicator.
    const id = highlighter.highlight(htmlEl, {
      style: action.style || 'pulse',
      duration: action.duration ?? 3500,
    });

    return {
      success: true,
      action,
      data: { highlightId: id, tag: htmlEl.tagName.toLowerCase() },
      timestamp: start,
      duration: Date.now() - start,
    };
  } catch (error) {
    return {
      success: false,
      action,
      error: error instanceof Error ? error.message : String(error),
      timestamp: start,
      duration: Date.now() - start,
    };
  }
}
